import React, { useState, useMemo } from "react";
import {
  TrashIcon,
  BuildingStorefrontIcon,
  PlusIcon,
  FunnelIcon,
} from "@heroicons/react/24/outline";
import { ColumnDef, RowSelectionState } from "@tanstack/react-table";
import CreativeForm from "../../components/forms/CreativeForm";
import {
  Creative,
  CreativeFormData,
  CreativeUpdateFormData,
} from "../../schemas/creativeSchema";
import {
  useCreativesInfinite,
  CreativesInfiniteFilters,
} from "../../hooks/useCreativesInfinite";
import CreativeFilterModal from "../../components/filters/CreativeFilterModal";
import DeleteCreativesModal from "../../components/modals/DeleteCreativesModal";
import { CreativeFilterFormData } from "../../schemas/creativeSchema";
import { formatTimestampToDate } from "../../utils/dateUtils";
import { useAuthPermissions } from "../../hooks/useAuthPermissions";
import { getChangedFields } from "../../utils/formUtils";
import Modal from "../../components/common/Modal";
import Dropdown from "../../components/common/Dropdown";
import Button from "../../components/common/Button";
import { useBrands } from "../../hooks/useBrands";
import { useBrandOptions } from "../../hooks/useBrandOptions";
import CreativeList, {
  CreativeDetailRow,
} from "../../components/subComponents/CreativeList";

const Creatives: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<Creative | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [filters, setFilters] = useState<CreativesInfiniteFilters>({});
  const [assignTargetIds, setAssignTargetIds] = useState<string[] | null>(null);
  const [assignBrandId, setAssignBrandId] = useState("");
  // Deleting detaches the creative from every playlist holding it, so the
  // confirmation has to show that reach — more than a window.confirm can.
  const [pendingDelete, setPendingDelete] = useState<{
    ids: string[];
    names: string[];
  } | null>(null);

  const { canDelete, canCreate, canView, canEdit } =
    useAuthPermissions("creatives");
  const { canView: canViewBrands } = useAuthPermissions("brands");

  // Assigning is a creatives.update operation, but the picker needs the
  // brand list — so both permissions have to line up before it is offered.
  const canAssignBrand = canEdit && canViewBrands;
  const { assignCreativesToBrand, isLoading: isBrandActionLoading } = useBrands(
    undefined,
    { enabled: false },
  );
  const { brandOptions, isBrandOptionsLoading } = useBrandOptions({
    enabled: canAssignBrand,
  });

  const {
    creatives,
    isLoading: isCreativesLoading,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    createCreative,
    updateCreative,
    deleteCreatives,
    isDeletingCreatives,
  } = useCreativesInfinite(filters);

  const creativeName = (creative: Creative) =>
    creative.media?.[0]?.fileName || creative.uniqueId;

  // Inserted after Name by CreativeList. Thumbnail, name, dates and status
  // are shared with the brand portal's list and defined there.
  const extraColumns: ColumnDef<Creative>[] = [
    {
      accessorKey: "adGroup",
      header: "Ad Group",
      cell: ({ row }) => row.original.adGroup?.name || "-",
    },
    {
      id: "brand",
      header: "Brand",
      cell: ({ row }) =>
        row.original.brandName ? (
          row.original.brandName
        ) : (
          <span className="text-gray-400">Unassigned</span>
        ),
    },
  ];

  const handleDelete = (creative: Creative) => {
    setPendingDelete({
      ids: [creative.id],
      names: [creativeName(creative)],
    });
  };

  const handleBulkDelete = (ids: string[]) => {
    const selected = creatives.filter((creative) => ids.includes(creative.id));
    setPendingDelete({ ids, names: selected.map(creativeName) });
  };

  const handleConfirmDelete = (ids: string[]) => {
    deleteCreatives(ids);
    setRowSelection({});
    setPendingDelete(null);
    // The detail pane would otherwise keep showing a creative that no longer
    // exists.
    if (selectedFile && ids.includes(selectedFile.id)) {
      setSelectedFile(null);
    }
  };

  const bulkActions = [
    ...(canAssignBrand
      ? [
          {
            label: "Assign to Brand",
            icon: <BuildingStorefrontIcon className="h-4 w-4" />,
            onClick: (ids: string[]) => {
              setAssignTargetIds(ids);
              setAssignBrandId("");
            },
            variant: "warning" as const,
          },
        ]
      : []),
    ...(canDelete
      ? [
          {
            label: "Delete Selected",
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: handleBulkDelete,
            variant: "danger" as const,
          },
        ]
      : []),
  ];

  const handleAssignSubmit = () => {
    if (!assignTargetIds) return;
    assignCreativesToBrand(
      {
        creativeIds: assignTargetIds,
        brandId: assignBrandId === "" ? null : assignBrandId,
      },
      {
        onSuccess: () => {
          setAssignTargetIds(null);
          setAssignBrandId("");
          setRowSelection({});
        },
      },
    );
  };

  const extraActions = [
    {
      label: "Delete",
      icon: <TrashIcon className="h-4 w-4" />,
      onClick: handleDelete,
      variant: "danger" as const,
      visible: canDelete,
    },
  ];

  const handleSubmit = async (
    data: CreativeFormData | CreativeUpdateFormData,
  ) => {
    if (selectedFile?.id) {
      const updateData = data as CreativeUpdateFormData;
      // Only send changed fields for update
      const changedFields = getChangedFields(updateData, {
        adGroupId: selectedFile.adGroupId || "",
        brandId: selectedFile.brandId || "",
        description: selectedFile.description || "",
        startDate: selectedFile.startDate || "",
        endDate: selectedFile.endDate || "",
      });
      await updateCreative({ id: selectedFile.id, creative: changedFields });
    } else {
      const createData = data as CreativeFormData;
      if (createData.file && createData.file.length > 0) {
        const files = Array.from(createData.file as ArrayLike<File>);
        await createCreative({
          ...createData,
          file: files,
        });
      }
    }

    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
  };

  const handleApplyFilter = (data: CreativeFilterFormData) => {
    // Helper function to check if a value is valid (not empty, null, or undefined)
    const isValidValue = (value: string | undefined): boolean => {
      return (
        value !== undefined &&
        value !== null &&
        value !== "" &&
        value.trim() !== ""
      );
    };

    // Filter out empty strings; infinite query auto-resets on filter change
    const cleanedData: CreativesInfiniteFilters = {};

    // Add values only if they are valid
    if (isValidValue(data.adGroupId)) {
      cleanedData.adGroupId = data.adGroupId;
    }

    if (isValidValue(data.brandId)) {
      cleanedData.brandId = data.brandId;
    }

    if (isValidValue(data.startDate)) {
      cleanedData.startDate = data.startDate;
    }

    if (isValidValue(data.endDate)) {
      cleanedData.endDate = data.endDate;
    }

    if (isValidValue(data.type)) {
      cleanedData.type = data.type;
    }

    if (isValidValue(data.status)) {
      cleanedData.status = data.status;
    }

    // Preserve search if exists
    if (filters.search) {
      cleanedData.search = filters.search;
    }
    setFilters(cleanedData);
    setIsFilterModalOpen(false);
  };
  const handleResetFilter = () => {
    setFilters({});
    setIsFilterModalOpen(false);
  };

  // Clear just filter-related values but keep modal open (used by Reset in modal)
  const handleClearFilterValues = () => {
    setFilters((prev) => ({
      // Preserve search term if present
      ...(prev.search ? { search: prev.search } : {}),
    }));
  };
  const handleCloseFilter = () => {
    setIsFilterModalOpen(false);
  };

  // Check if any filters are applied (excluding page, pageSize, search)
  const hasActiveFilters = useMemo(() => {
    return !!(
      filters.adGroupId ||
      filters.brandId ||
      filters.startDate ||
      filters.endDate ||
      filters.type ||
      filters.status
    );
  }, [filters]);

  const detailRows: CreativeDetailRow[] = selectedFile
    ? [
        { label: "Name", value: selectedFile.media?.[0]?.fileName || "-" },
        { label: "Ad Group", value: selectedFile.adGroup?.name || "-" },
        {
          label: "Start Date",
          value: selectedFile.startDate
            ? formatTimestampToDate(selectedFile.startDate)
            : "-",
        },
        {
          label: "End Date",
          value: selectedFile.endDate
            ? formatTimestampToDate(selectedFile.endDate)
            : "-",
        },
        {
          label: "Resolution",
          value: selectedFile.media?.[0]?.resolution || "N/A",
        },
        {
          label: "Frame Rate",
          value: selectedFile.media?.[0]?.frameRate || "N/A",
        },
        {
          label: "Upload Date",
          value: formatTimestampToDate(selectedFile.createdAt),
        },
        ...(selectedFile.description
          ? [{ label: "Description", value: selectedFile.description }]
          : []),
      ]
    : [];

  return (
    <div className="flex flex-col px-6 py-3">
      <div className="flex justify-between items-center mb-2">
        <h1 className="text-2xl font-bold text-gray-800">Creatives</h1>
        {canCreate && (
          <button
            onClick={() => {
              setSelectedFile(null);
              setIsModalOpen(true);
            }}
            className="p-2 bg-primary-default text-white rounded hover:bg-primary-light"
          >
            <PlusIcon className="h-6 w-6" />
          </button>
        )}
      </div>

      {canView && (
        <CreativeList
          creatives={creatives}
          isLoading={isCreativesLoading}
          hasNextPage={hasNextPage}
          isFetchingNextPage={isFetchingNextPage}
          onLoadMore={() => fetchNextPage()}
          onSearch={(searchTerm) =>
            setFilters((prev) => ({ ...prev, search: searchTerm }))
          }
          activeType={filters?.type}
          onTypeChange={(type) =>
            setFilters((prev) => {
              const { type: _dropped, ...rest } = prev;
              return type ? { ...rest, type } : rest;
            })
          }
          selectedCreative={selectedFile}
          onSelectCreative={setSelectedFile}
          extraColumns={extraColumns}
          extraActions={extraActions}
          bulkActions={bulkActions}
          selectable={canDelete || canAssignBrand}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          detailRows={detailRows}
          detailActions={
            canEdit && selectedFile ? (
              <button
                onClick={() => {
                  const formData = {
                    ...selectedFile,
                    adGroupId:
                      selectedFile.adGroupId || selectedFile.adGroup?.id,
                    startDate: selectedFile.startDate,
                    endDate: selectedFile.endDate,
                  };
                  setSelectedFile(formData as unknown as Creative);
                  setIsModalOpen(true);
                }}
                className="bg-gray-300 text-gray-800 px-4 py-1 rounded font-medium hover:bg-gray-400 transition"
              >
                Edit
              </button>
            ) : null
          }
          toolbarSlot={
            <button
              className="relative p-2 mx-2 bg-primary-default text-white rounded hover:bg-primary-light"
              onClick={() => setIsFilterModalOpen(true)}
            >
              <FunnelIcon className="h-5 w-5" />
              {hasActiveFilters && (
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500 ring-2 ring-white"></span>
              )}
            </button>
          }
        />
      )}

      {isModalOpen && (
        <CreativeForm
          isOpen={isModalOpen}
          initialData={selectedFile || undefined}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={isCreativesLoading}
        />
      )}

      <DeleteCreativesModal
        pending={pendingDelete}
        onCancel={() => setPendingDelete(null)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeletingCreatives}
      />

      {isFilterModalOpen && (
        <CreativeFilterModal
          visible={isFilterModalOpen}
          initialData={filters}
          onSubmit={handleApplyFilter}
          onCancel={handleResetFilter}
          onClose={handleCloseFilter}
          onReset={handleClearFilterValues}
        />
      )}

      <Modal
        isOpen={assignTargetIds !== null}
        onClose={() => setAssignTargetIds(null)}
        title="Assign to Brand"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            {assignTargetIds?.length === 1
              ? "This creative will be visible in the selected brand's portal."
              : `${assignTargetIds?.length ?? 0} creatives will be visible in the selected brand's portal.`}
          </p>
          <Dropdown
            label="Brand"
            options={[{ label: "Unassigned", value: "" }, ...brandOptions]}
            value={assignBrandId}
            name="assignBrandId"
            searchable
            onChange={(val) => setAssignBrandId(val as string)}
            disabled={isBrandOptionsLoading}
          />
          <div className="flex justify-end space-x-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setAssignTargetIds(null)}
              disabled={isBrandActionLoading}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleAssignSubmit}
              isLoading={isBrandActionLoading}
            >
              {assignBrandId ? "Assign" : "Unassign"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Creatives;
